import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useSession } from '../lib/session';
import { useAuth } from '../lib/auth-gate';
import { Logo } from './Logo';
import { Badge, Button, cn } from './ui';

/**
 * Application shell and navigation.
 *
 * The navigation is §51's tree in full, including the sections that arrive in
 * later phases. Those are shown and disabled rather than hidden: the shape of the
 * product is part of the design, and a CSM should be able to see that Product and
 * Insights are coming rather than wonder whether they exist somewhere else.
 */

interface NavItem {
  label: string;
  to?: string;
  /** Set when the destination is not built yet; names the phase. */
  later?: string;
  /**
   * Hidden unless the actor holds the matching capability. Each value maps to
   * the capability the destination actually needs — not to a blanket "is admin",
   * which would hide Account Ownership from CS Leadership even though they are
   * the people who manage assignments.
   */
  requires?: 'audit' | 'manage_users' | 'manage_assignments' | 'all_accounts' | 'review_ai_proposals';
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    label: '',
    items: [{ label: 'Home', to: '/' }],
  },
  {
    label: 'Customers',
    items: [
      { label: 'Enterprises', to: '/enterprises' },
      { label: 'Accounts', to: '/accounts' },
      { label: 'Contacts', to: '/contacts' },
      { label: 'Projects', to: '/projects' },
    ],
  },
  {
    label: 'Customer Success',
    items: [
      { label: 'My Work', to: '/my-work' },
      { label: 'AI Review', to: '/ai-review', requires: 'review_ai_proposals' },
      { label: 'Match Review', to: '/match-review', requires: 'review_ai_proposals' },
      { label: 'Commitments', to: '/commitments' },
      { label: 'Follow-ups', to: '/follow-ups' },
      { label: 'Cadence', to: '/cadence' },
      { label: 'Leadership', to: '/leadership', requires: 'all_accounts' },
      { label: 'Weekly Review', later: 'Phase 6' },
    ],
  },
  {
    label: 'Product',
    items: [
      { label: 'Inbox', later: 'Phase 7' },
      { label: 'Requests', later: 'Phase 7' },
      { label: 'Product Items', later: 'Phase 7' },
      { label: 'Roadmap', later: 'Phase 8' },
      { label: 'Releases', later: 'Phase 10' },
    ],
  },
  {
    label: 'Insights',
    items: [
      { label: 'CS Execution', later: 'Phase 6' },
      { label: 'Product Demand', later: 'Phase 7' },
      { label: 'Delivery Performance', later: 'Phase 9' },
    ],
  },
  {
    label: 'Admin',
    items: [
      { label: 'Users & Roles', to: '/admin/users', requires: 'manage_users' },
      { label: 'Account Ownership', to: '/admin/ownership', requires: 'manage_assignments' },
      { label: 'SLA & Cadence', to: '/admin/sla-cadence', requires: 'all_accounts' },
      { label: 'Audit', to: '/admin/audit', requires: 'audit' },
      { label: 'Product Modules', later: 'Phase 7' },
      { label: 'Integrations', later: 'Phase 4' },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  const permitted = (item: NavItem): boolean => {
    switch (item.requires) {
      case 'audit':
        return me.capabilities.canReadAudit;
      case 'manage_users':
        return me.capabilities.canManageUsers;
      case 'manage_assignments':
        return me.capabilities.canManageAssignments;
      case 'all_accounts':
        return me.capabilities.canSeeAllAccounts;
      case 'review_ai_proposals':
        return me.capabilities.canReviewAiProposals;
      default:
        return true;
    }
  };

  return (
    <div className="flex min-h-full">
      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 w-60 shrink-0 overflow-y-auto border-r border-border bg-surface transition-transform lg:static lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-14 items-center border-b border-border px-4">
          <div className="leading-tight">
            <Logo className="h-6" />
            <div className="mt-1 text-[10px] tracking-wide text-muted uppercase">
              CS &amp; Product Ops
            </div>
          </div>
        </div>

        <nav className="px-2 py-3">
          {NAV.map((group) => {
            const items = group.items.filter(permitted);
            if (items.length === 0) return null;
            return (
              <div key={group.label || 'root'} className="mb-3">
                {group.label && (
                  <div className="px-2 pb-1 text-[10px] font-semibold tracking-wider text-muted uppercase">
                    {group.label}
                  </div>
                )}
                <ul>
                  {items.map((item) => (
                    <li key={item.label}>
                      {item.to ? (
                        <NavLink
                          to={item.to}
                          end={item.to === '/'}
                          onClick={() => setNavOpen(false)}
                          className={({ isActive }) =>
                            cn(
                              'block rounded-md px-2 py-1.5 text-sm transition-colors',
                              isActive
                                ? 'bg-accent-soft font-medium text-ink'
                                : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
                            )
                          }
                        >
                          {item.label}
                        </NavLink>
                      ) : (
                        <div
                          title={`Arrives in ${item.later}`}
                          className="flex cursor-not-allowed items-center justify-between rounded-md px-2 py-1.5 text-sm text-muted"
                        >
                          {item.label}
                          <span className="text-[10px] text-muted">{item.later}</span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
      </aside>

      {navOpen && (
        <button
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-black/30 lg:hidden"
          onClick={() => setNavOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-border bg-page/85 px-4 backdrop-blur">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setNavOpen(true)}
              aria-label="Open navigation"
            >
              ☰
            </Button>
            <Breadcrumb path={location.pathname} />
          </div>
          <UserSwitcher />
        </header>

        <main className="min-w-0 flex-1 px-4 py-5 lg:px-6">{children}</main>
      </div>
    </div>
  );
}

function Breadcrumb({ path }: { path: string }) {
  const segments = path.split('/').filter(Boolean);
  const label = segments.length === 0 ? 'Home' : segments[0].replace(/-/g, ' ');
  return (
    <span className="truncate text-sm font-medium text-ink capitalize">{label}</span>
  );
}

/**
 * Development user switcher.
 *
 * The single most useful control on the screen while building this: the
 * authorization model is only convincing if you can watch the same page change
 * as you become a different person.
 */
function UserSwitcher() {
  const { me, devUsers, switchUser, isDevMode } = useSession();
  const auth = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative flex items-center gap-2">
      <div className="hidden text-right sm:block">
        <div className="text-xs font-medium text-ink">{me.fullName}</div>
        <div className="text-[10px] text-muted">{me.roles.join(' · ') || 'No roles'}</div>
      </div>
      <Badge tone={me.capabilities.canSeeAllAccounts ? 'info' : 'neutral'} dot={false}>
        {me.accountScope.unbounded
          ? 'All accounts'
          : `${me.accountScope.accountCount} account${me.accountScope.accountCount === 1 ? '' : 's'}`}
      </Badge>
      {/* Under SSO there is one identity and it is not selectable. */}
      {auth.signOut && (
        <Button size="sm" variant="secondary" onClick={auth.signOut}>
          Sign out
        </Button>
      )}
      {isDevMode && (
        <>
          <Button size="sm" variant="secondary" onClick={() => setOpen((v) => !v)}>
            Switch user
          </Button>
          {open && (
            <>
              <button
                className="fixed inset-0 z-40 cursor-default"
                aria-label="Close user list"
                onClick={() => setOpen(false)}
              />
              <div className="absolute top-full right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
                <div className="border-b border-border px-3 py-2">
                  <p className="text-xs font-semibold text-ink">Act as</p>
                  <p className="mt-0.5 text-[11px] text-ink-2">
                    Development only. Every page below reflects this person's permissions,
                    enforced server-side.
                  </p>
                </div>
                <ul className="max-h-80 overflow-y-auto">
                  {devUsers.map((user) => (
                    <li key={user.id}>
                      <button
                        onClick={() => {
                          switchUser(user.email);
                          setOpen(false);
                        }}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-surface-2',
                          user.email === me.email && 'bg-accent-soft',
                        )}
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-ink">{user.fullName}</span>
                          <span className="block truncate text-[11px] text-muted">
                            {user.title}
                          </span>
                        </span>
                        <span className="shrink-0 text-[10px] text-ink-2">
                          {user.roles.join(', ')}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
